import React from 'react';
import { SpaceOption, SpaceType } from './SpaceAssignments';
import { getSpaceLevelIcon } from './SpaceIcons';
import { SiteOption } from '@/hooks/useSiteHierarchyFilters';

export interface TreeNode {
  title: string;
  value: string;
  key: string;
  icon: React.ReactNode;
  children?: TreeNode[];
}

export const SELECT_ALL_VALUE = '__select_all__';

// Flatten site hierarchy into SpaceOption[] for tree building
export function flattenHierarchyToSpaceOptions(sites: SiteOption[]): SpaceOption[] {
  const options: SpaceOption[] = [];
  for (const site of sites) {
    options.push({ id: site.id, name: site.name, level: 'site' });
    for (const building of site.buildings || []) {
      options.push({ id: building.id, name: building.name, level: 'building', parentId: site.id });
      for (const floor of building.floors || []) {
        options.push({ id: floor.id, name: floor.name, level: 'floor', parentId: building.id });
        for (const room of floor.rooms || []) {
          options.push({ id: room.id, name: room.name, level: 'room', parentId: floor.id });
        }
        for (const zone of floor.zones || []) {
          options.push({ id: zone.id, name: zone.name, level: 'zone', parentId: floor.id });
        }
      }
    }
  }
  return options;
}

// Build tree data from flat space options with icons
export const buildTreeData = (spaces: SpaceOption[]): TreeNode[] => {
  const nodeMap = new Map<string, TreeNode>();
  const rootNodes: TreeNode[] = [];

  spaces.forEach((space) => {
    const icon = getSpaceLevelIcon(space.level);
    nodeMap.set(space.id, {
      title: space.name,
      value: space.id,
      key: space.id,
      icon: icon,
      children: [],
    });
  });

  spaces.forEach((space) => {
    const node = nodeMap.get(space.id);
    if (!node) return;
    if (space.parentId) {
      const parent = nodeMap.get(space.parentId);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(node);
      } else {
        rootNodes.push(node);
      }
    } else {
      rootNodes.push(node);
    }
  });

  const cleanNode = (node: TreeNode): TreeNode => {
    if (node.children && node.children.length === 0) {
      const { children: _, ...rest } = node;
      return rest as TreeNode;
    }
    if (node.children) {
      return { ...node, children: node.children.map(cleanNode) };
    }
    return node;
  };

  return rootNodes.map(cleanNode);
};

// Build tree data wrapped with a "Select All Spaces" root node
export const buildTreeDataWithSelectAll = (spaces: SpaceOption[]): TreeNode[] => {
  const nodes = buildTreeData(spaces);
  if (nodes.length === 0) return [];
  return [
    {
      title: 'Select All Spaces',
      value: SELECT_ALL_VALUE,
      key: SELECT_ALL_VALUE,
      icon: null as React.ReactNode,
      children: nodes,
    },
  ];
};

// Get default expanded keys (sites and buildings)
export const getDefaultExpandedKeys = (spaces: SpaceOption[]): string[] => {
  const keys: string[] = [SELECT_ALL_VALUE];
  spaces.forEach((space) => {
    if (space.level === 'site' || space.level === 'building') {
      keys.push(space.id);
    }
  });
  return keys;
};

// Resolve selected values to actual space IDs with levels for backend assignment
export const getSpaceIdsToAssign = (
  selectedSpaces: string[],
  availableSpaces: SpaceOption[],
): { id: string; level: SpaceType }[] => {
  if (selectedSpaces.includes(SELECT_ALL_VALUE)) {
    return availableSpaces
      .filter((s) => s.level === 'site')
      .map((s) => ({ id: s.id, level: s.level }));
  }
  return selectedSpaces
    .filter((v) => v !== SELECT_ALL_VALUE)
    .map((id) => {
      const space = availableSpaces.find((s) => s.id === id);
      return { id, level: (space?.level || 'room') as SpaceType };
    });
};
